import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { pullContactState } from "./store";
import Button from "../../Components/common/Button";
import { ReactComponent as PullContactsIcon } from "./svg/pullContacts.svg";
import { ReactComponent as ContactsFoundIcon } from "./svg/contactsFound.svg";
import PasteInput from "./PasteInput";
import ListSequence from "./ListSequence";
import LiAccountSelectDropdown from "./LiAccountSelectDropdown";
import userDetail from "Stores/userDetail";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Utils from "Utils/utils";
import { fetchLinkedInAccounts } from "Utils/commonAPI";

const liAccountLabel = account => {
  const { firstName = "", lastName = "", email = "", publicId = "" } =
    account || {};
  const name = `${firstName} ${lastName}`.trim();
  return name || email || publicId || "LinkedIn account";
};

function getTextAndButton() {
  let description = "";
  let buttonText = "Extract";

  const { pullContactsCount } = pullContactState;
  const { snHardLimit, advancedSnImportEnabled } = userDetail.userFeatureFlag;

  let smallNotes = `${Utils.formatNumberToStringInUSFormat(
    pullContactsCount
  )} contacts will be extracted`;

  // Event-attendees import: capped at LinkedIn's 1,000-per-search ceiling, and only
  // available when the connected account is attending the event.
  if (pullContactState.isEventUrl) {
    return {
      description: `Attendees of this event will be added to Salesgear (up to 1,000, LinkedIn's per-event limit). The connected LinkedIn account must be attending this event.`,
      buttonText: "Extract",
      smallNotes
    };
  }

  // Post-engagement import: simple copy, no Sales Navigator extraction limits.
  if (pullContactState.isPostUrl) {
    const parts = [];
    if (pullContactState.includeReactions) parts.push("reacted to");
    if (pullContactState.includeComments) parts.push("commented on");
    const who = parts.join(" or ") || "engaged with";
    return {
      description: `All ${Utils.formatNumberToStringInUSFormat(
        pullContactsCount
      )} people who ${who} this post will be added to Salesgear.`,
      buttonText: "Extract",
      smallNotes
    };
  }

  const enterpriseLimit = snHardLimit || 25000;
  const enterpriseLimitInString =
    Utils.formatNumberToStringInUSFormat(enterpriseLimit);

  if (advancedSnImportEnabled) {
    if (pullContactsCount <= enterpriseLimit) {
      description =
        "All the contacts from this search will be extracted to Salesgear.";
    } else if (
      pullContactsCount > enterpriseLimit &&
      pullContactsCount <= 50000
    ) {
      description = `Your plan allows up to ${enterpriseLimitInString} contacts per search. We'll extract the first ${enterpriseLimitInString} from this result.`;
      smallNotes = `${enterpriseLimitInString} out of ${Utils.formatNumberToStringInUSFormat(
        pullContactsCount
      )} contacts will be extracted`;
      buttonText = `Extract ${enterpriseLimitInString} Contacts now`;
    } else {
      description = `Your plan supports extracting up to <b>${enterpriseLimitInString}</b>, but only from Sales Navigator searches with <b>50,000</b> results or fewer.
Please narrow your Sales Navigator search to below <b>50,000</b> contacts and try again.`;
    }
  } else if (pullContactsCount >= 2500) {
    description = `You can extract up to 2,500 contacts from this Sales Navigator search. Upgrade to unlock higher extraction limits per search.`;
    buttonText = "Extract 2,500 Contacts now";
    smallNotes = `2,500 out of ${Utils.formatNumberToStringInUSFormat(
      pullContactsCount
    )} contacts will be extracted`;
  } else {
    description = ` All ${Utils.formatNumberToStringInUSFormat(
      pullContactsCount
    )} contacts will be pulled from Sales Navigator into Salesgear.`;
  }

  return {
    description,
    buttonText,
    smallNotes
  };
}

const PullContacts = observer(() => {
  const history = useHistory();
  const handleSearch = () => {
    // For a post, at least one of reactions/comments must be selected.
    if (
      pullContactState.isPostUrl &&
      !pullContactState.includeReactions &&
      !pullContactState.includeComments
    ) {
      return;
    }
    pullContactState.setSearchLoading(true);
    pullContactState.lookupContacts(history);
  };

  const handleExtract = () => {
    pullContactState.setExtractBtnLoading(true);
    pullContactState.extractContacts(history);
  };

  const handleShowAddToSeq = () => {
    pullContactState.setShowListSeq(true);
  };

  const handleBack = () => {
    // From the list/sequence step, step back to the "found" summary; from the
    // summary, step back to the URL input (keeping the pasted URL so it can be edited).
    if (pullContactState.showListSeq) {
      pullContactState.setShowListSeq(false);
      return;
    }
    pullContactState.setExtractBtn(false);
    pullContactState.setPullContactsCount(0);
    pullContactState.setListInfo({});
    pullContactState.setSelectedSequence({});
    pullContactState.setAddToListChecked(false);
    pullContactState.setAddToSeqChecked(false);
    pullContactState.setDisableExtractBtn(false);
    pullContactState.setCreateListError("");
  };

  const closeCbk = () => {
    pullContactState.setShowPopup(false);
    pullContactState.resetFunc();
  };

  const getSequenceList = async (pageNo = 1) => {
    const payload = {
      pageNo: pageNo || 1,
      limit: 500,
      filters: {
        memberIds: [userDetail?.userInfo?.memberId]
      }
    };
    let seqObjResponse = await pullContactState.getSequenceData(payload);
    const { totalPages = 0, currentPage = 0 } = seqObjResponse || {};
    if (totalPages > 0 && currentPage < totalPages) {
      getSequenceList(currentPage + 1);
    }
    return true;
  };

  useEffect(() => {
    pullContactState.getContactList();
    getSequenceList();
    fetchLinkedInAccounts();
  }, []);

  useEffect(() => {
    pullContactState.initLiAccountSelection();
  }, [pullContactState.activeLiAccounts.length]);

  const liAccountOptions = pullContactState.activeLiAccounts.map(account => ({
    id: account.id,
    name: liAccountLabel(account),
    profilePictureUrl: account.profilePictureUrl,
    isSalesNavigator: account.linkedinAccountType === "SALES_NAVIGATOR"
  }));

  const handleSelectLiAccount = item => {
    pullContactState.setSelectedLiAccountId(item?.id || "");
  };

  const { pullContactsCount } = pullContactState;

  const { description, buttonText, smallNotes } = getTextAndButton();

  const enterpriseLimit = userDetail.userFeatureFlag.snHardLimit || 25000;

  return (
    pullContactState.showPopup && (
      <>
        <div className="pullContactsMask" onClick={closeCbk} />
        <div className="pullContactsWrapper">
          <div className="card">
            <i className="material-icons closeIcon" onClick={closeCbk}>
              close
            </i>
            <PullContactsIcon />
            <p className="pullContactsText">Import contacts from LinkedIn</p>
            <p className="importSubText">
              Paste a Sales Navigator search, a LinkedIn post, or a LinkedIn
              event URL. We'll figure out the rest.
            </p>
            {pullContactState.activeLiAccounts.length > 1 &&
              !pullContactState.extractBtn && (
                <div className="liAccountSelectWrapper">
                  <p className="liAccountSelectLabel">
                    Import using LinkedIn account
                  </p>
                  <LiAccountSelectDropdown
                    optionList={liAccountOptions}
                    value={pullContactState.selectedLiAccountId}
                    cbk={handleSelectLiAccount}
                  />
                  {pullContactState.needsLiAccountSelection && (
                    <p className="liAccountSelectNote">
                      Select which LinkedIn account to import with.
                    </p>
                  )}
                </div>
              )}
            <PasteInput />
            {pullContactState.isEventUrl && !pullContactState.extractBtn && (
              <p className="eventAttendingNote">
                The connected LinkedIn account must be attending this event to
                import its attendees. Up to 1,000 attendees can be imported.
              </p>
            )}
            {pullContactState.isPostUrl && !pullContactState.extractBtn && (
              <div className="engagementCheckboxes">
                <p className="engagementCheckboxTitle">
                  Who do you want to import from this post?
                </p>
                <label className="engagementCheckbox">
                  <input
                    type="checkbox"
                    checked={pullContactState.includeReactions}
                    onChange={e =>
                      pullContactState.setIncludeReactions(e.target.checked)
                    }
                  />
                  <span>People who reacted</span>
                </label>
                <label className="engagementCheckbox">
                  <input
                    type="checkbox"
                    checked={pullContactState.includeComments}
                    onChange={e =>
                      pullContactState.setIncludeComments(e.target.checked)
                    }
                  />
                  <span>People who commented</span>
                </label>
              </div>
            )}
            {pullContactState.needsSalesNavAccount &&
              !pullContactState.extractBtn && (
                <p className="salesNavRequiredNote">
                  Importing from a Sales Navigator search needs a connected Sales
                  Navigator account. Paste a LinkedIn post or event URL to import
                  with a regular account.
                </p>
              )}
            {pullContactState.extractBtn ? (
              <>
                <div
                  className={`extractContactsWrapper ${
                    pullContactState.showListSeq ? "showListSeq" : ""
                  }`}
                >
                  <div className="extractTextWithNotes">
                    <ContactsFoundIcon />
                    <div className="flexWrapperWithIcon">
                      {pullContactState.showListSeq ? (
                        <p className="extractContactsText">{smallNotes}</p>
                      ) : (
                        <p className="extractContactsText">
                          <strong>{`${Utils.formatNumberToStringInUSFormat(
                            pullContactsCount
                          )} ${
                            pullContactsCount > 1 ? "contacts" : "contact"
                          } `}</strong>{" "}
                          <span>{` found`}</span>
                        </p>
                      )}
                      {!pullContactState.showListSeq && (
                        <div className="flexWrapper">
                          <p
                            className="smallNotesText"
                            dangerouslySetInnerHTML={{ __html: description }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {pullContactState.showListSeq && (
                    <div className="extractBtnWrapper">
                      <div className="listSequenceContainer">
                        <ListSequence />
                      </div>
                    </div>
                  )}
                </div>
                {userDetail.userFeatureFlag.advancedSnImportEnabled &&
                pullContactsCount > 50000 &&
                !pullContactState.isEventUrl &&
                !pullContactState.isPostUrl ? (
                  <Button
                    btnText="Adjust Search in Sales Navigator"
                    onClick={() => {
                      window.open(pullContactState.pasteInputValue, "_blank");
                    }}
                    className="extractBtn"
                  />
                ) : (
                  <div className="buttonWrapper">
                    <Button
                      btnText="Back"
                      onClick={handleBack}
                      className="backBtn"
                    />
                    {/* Sales Navigator per-search-limit upsell only — not applicable to
                        post or event imports (events are hard-capped at 1,000 by LinkedIn,
                        so there is nothing an upgrade can "unlock"). */}
                    {!pullContactState.isEventUrl &&
                      !pullContactState.isPostUrl &&
                      ((pullContactsCount > 2500 &&
                        !userDetail.userFeatureFlag.advancedSnImportEnabled) ||
                        (pullContactsCount > enterpriseLimit &&
                          userDetail.userFeatureFlag
                            .advancedSnImportEnabled)) && (
                        <>
                          <Button
                            btnText={`Unlock All ${Utils.formatNumberToStringInUSFormat(
                              pullContactsCount
                            )} Contacts`}
                            onClick={() => {
                              window.Intercom("show");
                              pullContactState.resetFunc();
                            }}
                            className="extractAll"
                          />
                        </>
                      )}
                    <Button
                      btnText={buttonText}
                      onClick={
                        !pullContactState.showListSeq
                          ? handleShowAddToSeq
                          : handleExtract
                      }
                      className={`extractBtn ${
                        pullContactState.disableExtractBtn
                          ? "disableExtractBtn"
                          : ""
                      } ${
                        pullContactState?.extractBtnLoading ? "loading" : ""
                      }`}
                      loader={pullContactState.extractBtnLoading}
                    />
                  </div>
                )}
              </>
            ) : (
              <Button
                btnText={"Search"}
                onClick={handleSearch}
                disabled={
                  pullContactState.needsSalesNavAccount ||
                  pullContactState.needsLiAccountSelection ||
                  (pullContactState.isPostUrl &&
                    !pullContactState.includeReactions &&
                    !pullContactState.includeComments)
                }
                className={`searchWrapper ${
                  pullContactState?.searchLoading ? "loading" : ""
                } ${
                  pullContactState.needsSalesNavAccount ||
                  pullContactState.needsLiAccountSelection ||
                  (pullContactState.isPostUrl &&
                    !pullContactState.includeReactions &&
                    !pullContactState.includeComments)
                    ? "disableExtractBtn"
                    : ""
                }`}
                loader={pullContactState.searchLoading}
              />
            )}
          </div>
        </div>
      </>
    )
  );
});

export { PullContacts };
export default PullContacts;
